import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
    Grid,
    IconButton,
    Paper,
    Tooltip,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import clsx from "clsx";
import { Routes } from "components/Navigation/Routes";
import AcxMicroPlayer from "components/UI/Audio/AcxMicroPlayer";
import { observer } from "mobx-react";
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ConversationData } from "services/ConversationService";
import { useStore } from "utils/useStore";
import ConversationsStore from "../../../Stores/ConversationsStore";
import ConversationSummary from "./ConversationSummary";
import { FavoriteToggle } from "components/UI/FavoriteToggle";

type Props = {
    conversation: ConversationData;
};

const UnfocusedConversation: React.FC<Props> = observer(({ conversation }) => {
    const store = useStore(ConversationsStore);

    const navigate = useNavigate();
    const theme = useTheme();

    const hideEvalActionButton =
        store.authStore.canUserView("Restrict Evaluations") &&
        !store.authStore.isUserUltra();

    const agentFullName = conversation.agentFirstName
        ? conversation.agentFirstName + " " + conversation.agentLastName
        : "Unknown Agent";

    function getEvalActionButton() {
        let evalActionButton = (
            <IconButton sx={{ p: 0 }} disabled size="large">
                <AddCircleOutlineIcon color="disabled" />
            </IconButton>
        );

        if (conversation.evalType) {
            evalActionButton = (
                <IconButton
                    sx={{ p: 0 }}
                    onClick={() => {
                        if (
                            store.authStore.orgStore.selectedOrganization?.id &&
                            conversation.evaluationId
                        ) {
                            navigate(
                                Routes.makeEvaluationRoute(
                                    store.authStore.orgStore
                                        .selectedOrganization?.id,
                                    conversation.evaluationId,
                                ),
                            );
                        }
                    }}
                    size="large"
                >
                    <CheckCircleOutlineIcon
                        color={
                            conversation.evalType === "MiddleOfTheFunnel"
                                ? "primary"
                                : "secondary"
                        }
                    />
                </IconButton>
            );
        } else if (
            !!store.authStore.orgStore.selectedOrganization &&
            store.authStore.canUserEdit("Sample Single Conversation")
        ) {
            evalActionButton = (
                <Tooltip
                    title={
                        conversation.evalType === "MiddleOfTheFunnel"
                            ? "AI Evaluation"
                            : conversation.evalType
                            ? "Human Evaluation"
                            : !!store.authStore.orgStore.selectedOrganization
                            ? "Sample this Conversation"
                            : "No Evaluation"
                    }
                >
                    <IconButton
                        id="conversations-sample-this-conversation"
                        sx={{ p: 0 }}
                        onClick={() => {
                            store.getSingleConversation(
                                conversation.conversationId,
                            );
                            store.toggleEvaluateConversationDialogOpen();
                        }}
                        size="large"
                    >
                        <AddCircleOutlineIcon color="disabled" />
                    </IconButton>
                </Tooltip>
            );
        }

        return evalActionButton;
    }

    const isLargeScreen = useMediaQuery(theme.breakpoints.up("md"));
    const spacing = isLargeScreen ? 1 : 0;

    const userCannotViewMedia =
        !store.authStore.isUserUltra() &&
        store.authStore.canUserView("Restrict Media");

    return (
        <Paper
            sx={{
                borderRadius: 2,
                mt: 1,
                mb: 1,
                boxShadow: "0px 0px 8px 0px rgba(0, 0, 0, 0.05)",
            }}
            elevation={0}
            data-testid="unfocused-conversation-card"
        >
            <Grid
                item
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                }}
            >
                <Grid
                    container
                    className={clsx("pendo-ignore")}
                    item
                    xs={11}
                    sx={{ cursor: "pointer", minHeight: "40px" }}
                >
                    <Link
                        className="pendo-ignore"
                        to={`${conversation.conversationId}/`}
                        style={{ textDecoration: "none", width: "100%" }}
                        onClick={() => {
                            store.setSelectedConversationId(
                                conversation.conversationId,
                            );
                        }}
                    >
                        <ConversationSummary
                            summary={conversation.summary}
                            callDurationMillis={conversation.callDurationMillis}
                            timestamp={conversation.timestamp}
                            conversationClassifiers={
                                conversation.conversationClassifiers
                            }
                            agentFullName={agentFullName}
                            selectedClassifierIds={
                                store.selectedContainsClassifierIds
                            }
                            highlights={conversation.highlights}
                            hasTranslation={conversation.hasTranslation}
                            nonEnglish={conversation.nonEnglish}
                            isChat={
                                conversation.mediaType === 6 &&
                                conversation.callDurationMillis === 0
                            }
                            chatMessageCount={
                                conversation.chatTranscription?.length
                            }
                        />
                    </Link>
                </Grid>
                <Grid
                    item
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexDirection: "column",
                        paddingX: 1,
                        paddingTop: 1,
                        paddingBottom: 0.5,
                    }}
                >
                    <Grid
                        item
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            gap: spacing,
                        }}
                    >
                        {!hideEvalActionButton && (
                            <Grid item>{getEvalActionButton()}</Grid>
                        )}

                        <Grid item>
                            <FavoriteToggle
                                variant="noBorder"
                                id="favorite-conversation"
                                favoriteFn={() => {
                                    store.addFavorite(
                                        conversation.conversationId,
                                    );
                                }}
                                unfavoriteFn={() => {
                                    store.removeFavorite(
                                        conversation.conversationId,
                                    );
                                }}
                                isFavorited={store.hasFavorite(
                                    conversation.conversationId,
                                )}
                            />
                        </Grid>
                    </Grid>
                    {!!conversation.callDurationMillis && !userCannotViewMedia && (
                        <Grid sx={{ marginRight: -0.5 }}>
                            <AcxMicroPlayer
                                downloadMediaFileId={
                                    conversation.conversationId
                                }
                                mediaSourceType="audioFile"
                                style={{
                                    color: "#3364D6",
                                }}
                                iconHeight={48}
                                iconWidth={48}
                            />
                        </Grid>
                    )}
                </Grid>
            </Grid>
        </Paper>
    );
});

export default UnfocusedConversation;
